/*
 * @providesModule StaticWarningModal
 *
 */

import FTUEStyles from 'FTUEStyles';
import HTMLText from 'HTMLText';
import Label from 'Label';
import ModalNavigationController from 'ModalNavigationController';
import Optional from 'Optional';
import PushButton from 'PushButton';
import React from 'react';
import PropTypes from 'prop-types';
import styles from 'StaticWarningModalStyle';
import SkillzImage from 'SkillzImage';
import SkillzStyle from 'SkillzStyle';
import StaticContainer from 'react-static-container';
import Sizes from 'Sizes';

import {Platform, Text, TouchableOpacity, View} from 'react-native';

const buttonBorderRadius = SkillzStyle.fullStyles.viewCornerRadiusPrimary.borderRadius;
const {btnGradientOne, btnGradientTwo} = SkillzStyle.fullStyles;

export default class StaticWarningModal extends React.Component {
  static propTypes = {
    icon: PropTypes.string,
    title: PropTypes.string,
    message: PropTypes.string.isRequired,
    buttonBorderRadius: PropTypes.number,
    buttonColors: PropTypes.arrayOf(PropTypes.string),
    buttonTitleArray: PropTypes.arrayOf(PropTypes.string).isRequired,
    buttonTestIDArray: PropTypes.arrayOf(PropTypes.string),
    callback: PropTypes.func.isRequired,
    isCell: PropTypes.bool,
    onDismiss: PropTypes.func,
    onPress: PropTypes.func,
    cancelButtonAccessibilityLabel: PropTypes.string,
    linkAccessibilityLabel: PropTypes.string,
    linkTestID: PropTypes.string,
    messageAccessibilityLabel: PropTypes.string,
    messageTestID: PropTypes.string,
    titleAccessibilityLabel: PropTypes.string,
    titleTestID: PropTypes.string,
    titleTextStyling: Text?.propTypes?.style,
    messageTextStyling: Text?.propTypes?.style,
    buttonTextStyling: Text?.propTypes?.style,
    ctaButtonStyling: PushButton.propTypes.style,
    buttonContainerStyling: PropTypes.style,
    hasTitleMessageDivider: PropTypes.bool,
    renderAdditionalComponent: PropTypes.func,
    renderCustomView: PropTypes.func,
  };

  static defaultProps = {
    titleTextStyling: styles.title,
    messageTextStyling: styles.message,
    buttonTextStyling: styles.buttonText,
    hasTitleMessageDivider: false,
    renderAdditionalComponent: null,
    renderCustomView: null,
    titleAccessibilityLabel: 'header',
    messageAccessibilityLabel: 'message',
  };

  static createModal(
    icon: string,
    title: string,
    message: string,
    buttonTitleArray: Array,
    callback: (index: number) => void,
    onDismiss: () => void = null
  ): React.ReactNode {
    return (
      <StaticWarningModal
        icon={icon}
        title={title}
        message={message}
        buttonTitleArray={buttonTitleArray}
        callback={callback.bind(this)}
        onDismiss={onDismiss}
      />
    );
  }

  _dismiss() {
    if (this.props.onDismiss) {
      this.props.onDismiss();
    }
    ModalNavigationController.hideModal();
  }

  _renderButtons(): React.ReactNode {
    let cancelButton = null;
    const numButtons = this.props.buttonTitleArray.length;
    const title = this.props.buttonTitleArray[numButtons - 1];

    const CTAButton = (
      <PushButton
        style={
          numButtons == 1 ? [styles.CTAButton, this.props.ctaButtonStyling] : styles.CTAButtons
        }
        accessible={true}
        accessibilityRole={'button'}
        accessibilityLabel={title}
        testID={this.props.buttonTestIDArray?.[numButtons - 1]}
        onPress={this.props.callback.bind(this, numButtons - 1)}
        cornerRadius={this.props.buttonBorderRadius ?? buttonBorderRadius}
        textStyle={this.props.buttonTextStyling}
        colors={this.props.buttonColors ?? btnGradientOne}
        title={title}
      />
    );
    if (numButtons == 2) {
      const accessibilityLabel = this.props.cancelButtonAccessibilityLabel || 'Cancel';
      cancelButton = (
        <PushButton
          style={styles.CTAButtons}
          accessible={true}
          accessibilityLabel={accessibilityLabel}
          accessibilityRole={'button'}
          testID={this.props.buttonTestIDArray?.[0]}
          onPress={this.props.callback.bind(this, 0)}
          cornerRadius={this.props.buttonBorderRadius ?? buttonBorderRadius}
          textStyle={this.props.buttonTextStyling}
          colors={this.props.buttonColors ?? btnGradientTwo}
          title={this.props.buttonTitleArray[0]}
        />
      );
    }

    return (
      <View style={[styles.buttonContainer, this.props.buttonContainerStyling]}>
        {cancelButton}
        {CTAButton}
      </View>
    );
  }

  _renderCellWarningModal(): React.ReactNode {
    const minContentHeight = 550;
    const minHeight = minContentHeight * 0.4; // eslint-disable-line no-magic-numbers
    return (
      <View
        style={[
          styles.contentContainer,
          {minHeight: minHeight, paddingBottom: 10, paddingTop: 10, width: Sizes.screenWidth},
        ]}
      >
        <SkillzImage
          source={this.props.icon}
          style={styles.cellIcon}
          resizeMode={'contain'}
          key={'image_icon'}
        />
        <Label style={[this.props.titleTextStyling, {paddingTop: 5}]}>{this.props.title}</Label>
        <Label style={this.props.messageTextStyling} adjustsFontSizeToFit={false} numberOfLines={0}>
          {this.props.message}
        </Label>
        {this._renderButtons()}
      </View>
    );
  }

  _renderDivider(): React.ReactNode | null {
    return <View style={styles.divider} />;
  }

  _renderStaticWarningModal(): React.ReactNode {
    const noIcon = this.props.icon == null;
    const minContentHeight = Platform.select({native: 550, web: 1000});
    // eslint-disable-next-line no-magic-numbers
    const minHeight = noIcon ? minContentHeight * 0.3 : minContentHeight * 0.4;
    const paddingTop = noIcon ? 0 : 24; // eslint-disable-line no-magic-numbers
    const linkAccesssibility = this.props.onPress
      ? {
          accessibilityLabel: this.props.linkAccessibilityLabel,
          accessibilityRole: 'link',
          accessible: true,
          testID: this.props.linkTestID,
        }
      : {accessible: false};

    return (
      <View
        style={[
          styles.contentContainer,
          {minHeight: minHeight, paddingBottom: 10, paddingTop: 10, width: Sizes.screenWidth},
        ]}
        testID={this.props.testID}
      >
        <Optional test={!noIcon}>
          <SkillzImage
            source={this.props.icon}
            style={styles.cellIcon}
            resizeMode={'contain'}
            key={'image_icon'}
          />
        </Optional>
        <Label
          accessibilityLabel={this.props.titleAccessibilityLabel}
          testID={this.props.titleTestID}
          style={[this.props.titleTextStyling, {paddingTop}]}
        >
          {this.props.title}
        </Label>
        <Optional test={this.props.hasTitleMessageDivider}>{this._renderDivider()}</Optional>
        <TouchableOpacity
          disabled={!this.props.onPress}
          {...linkAccesssibility}
          onPress={() => {
            this.props.onPress();
          }}
        >
          <HTMLText
            style={this.props.messageTextStyling}
            accessibilityLabel={this.props.messageAccessibilityLabel}
            testID={this.props.messageTestID}
            containerStyle={this.props.messageTextStyling}
            adjustsFontSizeToFit={false}
            numberOfLines={0}
            html={this.props.message}
          />
        </TouchableOpacity>
        <Optional test={this.props.renderCustomView}>{this.props.renderCustomView?.()}</Optional>
        {this._renderButtons()}
        <Optional test={this.props.renderAdditionalComponent}>
          {this.props.renderAdditionalComponent?.()}
        </Optional>
      </View>
    );
  }

  render(): React.ReactNode {
    if (this.props.isCell) {
      return (
        <StaticContainer>
          <View
            style={[
              FTUEStyles.fullScreenContainer,
              FTUEStyles.dropShadowContainer,
              styles.screenContainer,
            ]}
          >
            {this._renderCellWarningModal()}
          </View>
        </StaticContainer>
      );
    } else {
      return (
        <StaticContainer>
          <View
            style={[
              FTUEStyles.fullScreenContainer,
              FTUEStyles.dropShadowContainer,
              styles.screenContainer,
            ]}
          >
            {this._renderStaticWarningModal()}
          </View>
        </StaticContainer>
      );
    }
  }
}
